import { useMemo, type ComponentType } from "react";
import { History, Lock, Mail, MessageSquare } from "lucide-react";
import { Body, Caption, EmptyState, VBadge } from "@/components/ui";
import type { Dossier, ReservationRequest, V2HistoryEntry } from "@/v2/shared/model";
import { formatDateTime } from "@/v2/shared/format";

// Eén tijdlijn per aanvraag (CB-10/CB-15/CB-19/CB-20): opmerkingen (interne
// notities, geplakte binnengekomen mails en terugkoppeling naar de
// aanvrager), automatische systeemmails en de acties uit de historiek,
// chronologisch samengevoegd. Een binnengekomen mail wordt als opmerking
// geplakt; er is bewust geen aparte oplaad- of verstuurknop.
type FeedItem =
  | {
      kind: "mail";
      id: string;
      at: string;
      author: string;
      subject: string;
      body: string;
    }
  | {
      kind: "comment";
      id: string;
      at: string;
      author: string;
      text: string;
      mailed: boolean;
      internal: boolean;
    }
  | { kind: "event"; id: string; at: string; author: string; action: string };

type Tone = "primary" | "info" | "muted";

const toneRing: Record<Tone, string> = {
  primary: "bg-primary-50 text-primary-700",
  info: "bg-info-50 text-info-700",
  muted: "bg-muted text-muted-foreground",
};

// Gedeelde tijdlijn-opmaak: een verticale lijn die de gebeurtenissen verbindt,
// met een gekleurde stip per soort. De lijn loopt achter de stippen door en
// wordt door de ring-background rond elke stip netjes onderbroken.
function TimelineRow({
  icon: Icon,
  tone,
  isLast,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  tone: Tone;
  isLast: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <li className="relative flex gap-4 pb-5 last:pb-0">
      {!isLast ? (
        <span
          className="absolute left-4 top-8 bottom-0 w-px -translate-x-1/2 bg-border"
          aria-hidden
        />
      ) : null}
      <span
        className={`relative z-10 mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full ring-4 ring-background ${toneRing[tone]}`}
      >
        <Icon className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}

// Kale historiek-tijdlijn (CB-20), los van de communicatie. Wordt op het
// dossierdetail gebruikt; de aanvraag toont alles samen in de
// CommunicationTab hieronder.
export function HistoryFeed({
  history,
}: {
  history: ReadonlyArray<V2HistoryEntry>;
}): React.JSX.Element {
  if (history.length === 0) {
    return <EmptyState title="Nog geen historiek" description="Er zijn nog geen acties gelogd." />;
  }
  const items = [...history].sort((a, b) => (a.at < b.at ? 1 : -1));
  return (
    <ol className="relative">
      {items.map((item, index) => (
        <TimelineRow key={item.id} icon={History} tone="muted" isLast={index === items.length - 1}>
          <EventBody author={item.author} at={item.at} action={item.action} />
        </TimelineRow>
      ))}
    </ol>
  );
}

// Zichtbaarheid (CB-15): opmerkingen op de aanvraag zijn voor aanvrager én
// beheerder; interne dossier-opmerkingen (via `dossier`) verschijnen alleen
// in de feed op het dossierdetail, dat beheerder-only is.
export function CommunicationTab({
  request,
  dossier,
}: {
  request: ReservationRequest;
  dossier?: Dossier;
}): React.JSX.Element {
  const feed = useMemo<Array<FeedItem>>(() => {
    const mails: Array<FeedItem> = request.communications.map((m) => ({
      kind: "mail",
      id: m.id,
      at: m.at,
      author: m.author,
      subject: m.subject,
      body: m.body,
    }));
    const comments: Array<FeedItem> = request.comments.map((c) => ({
      kind: "comment",
      id: c.id,
      at: c.createdAt,
      author: c.author,
      text: c.text,
      mailed: c.kind === "question",
      internal: false,
    }));
    const dossierComments: Array<FeedItem> = (dossier?.comments ?? []).map((c) => ({
      kind: "comment",
      id: c.id,
      at: c.createdAt,
      author: c.author,
      text: c.text,
      mailed: false,
      internal: true,
    }));
    const events: Array<FeedItem> = request.history.map((h) => ({
      kind: "event",
      id: h.id,
      at: h.at,
      author: h.author,
      action: h.action,
    }));
    return [...mails, ...comments, ...dossierComments, ...events].sort((a, b) =>
      a.at < b.at ? 1 : -1,
    );
  }, [request.communications, request.comments, dossier, request.history]);

  if (feed.length === 0) {
    return (
      <EmptyState title="Nog geen activiteit" description="Er is nog geen communicatie of historiek." />
    );
  }

  return (
    <ol className="relative">
      {feed.map((item, index) => {
        const isLast = index === feed.length - 1;
        if (item.kind === "mail") {
          return (
            <TimelineRow key={item.id} icon={Mail} tone="primary" isLast={isLast}>
              <article className="rounded-xl border border-border bg-card px-4 py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <Body weight="medium">{item.subject}</Body>
                  <Caption tone="muted">Verzonden · {formatDateTime(item.at)}</Caption>
                </div>
                <Body className="mt-1.5 whitespace-pre-line text-muted-foreground">{item.body}</Body>
                <Caption tone="muted" className="mt-2 block">
                  {item.author}
                </Caption>
              </article>
            </TimelineRow>
          );
        }
        if (item.kind === "comment") {
          return (
            <TimelineRow key={item.id} icon={MessageSquare} tone="info" isLast={isLast}>
              <article className="rounded-xl border border-border bg-card px-4 py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <span className="inline-flex items-center gap-2">
                    <Body weight="medium">Opmerking</Body>
                    {item.internal ? (
                      <VBadge color="neutral" size="count" className="gap-1">
                        <Lock className="size-3" aria-hidden />
                        Intern · dossier
                      </VBadge>
                    ) : null}
                  </span>
                  <Caption tone="muted">{formatDateTime(item.at)}</Caption>
                </div>
                <Body className="mt-1.5 whitespace-pre-line text-muted-foreground">{item.text}</Body>
                <Caption tone="muted" className="mt-2 flex items-center gap-1.5">
                  {item.author}
                  {item.mailed ? (
                    <span className="inline-flex items-center gap-1 text-info-700">
                      · <Mail className="size-3" aria-hidden /> per mail naar de aanvrager verzonden
                    </span>
                  ) : null}
                </Caption>
              </article>
            </TimelineRow>
          );
        }
        return (
          <TimelineRow key={item.id} icon={History} tone="muted" isLast={isLast}>
            <EventBody author={item.author} at={item.at} action={item.action} />
          </TimelineRow>
        );
      })}
    </ol>
  );
}

// Lichte, inline weergave voor een historiek-actie: geen kaart, zodat de
// systeemgebeurtenissen visueel ondergeschikt blijven aan de opmerkingen en
// mails in de tijdlijn.
function EventBody({
  author,
  at,
  action,
}: {
  author: string;
  at: string;
  action: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-1">
      <Body className="text-muted-foreground">{action}</Body>
      <Caption tone="muted">
        {author} · {formatDateTime(at)}
      </Caption>
    </div>
  );
}
